import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { QuestionCard } from './QuestionCard';
import { TestConfigModal } from './TestConfigModal';
import { useFilter } from '../contexts/FilterContext';
import { BookOpenIcon, ChevronRightIcon, ArrowLeftIcon } from './icons/InterfaceIcons';

const papersData = [
  { "shift": "NEET 2013", "occurrences": 180 },
  { "shift": "NEET 2014", "occurrences": 180 },
  { "shift": "NEET 2015", "occurrences": 180 },
  { "shift": "NEET 2016 (Phase 1)", "occurrences": 180 },
  { "shift": "NEET 2016 (Phase 2)", "occurrences": 180 },
  { "shift": "NEET 2017", "occurrences": 180 },
  { "shift": "NEET 2018", "occurrences": 180 },
  { "shift": "NEET 2019", "occurrences": 180 },
  { "shift": "NEET 2020", "occurrences": 180 },
  { "shift": "NEET 2021", "occurrences": 180 },
  { "shift": "NEET 2022", "occurrences": 180 },
  { "shift": "NEET 2023", "occurrences": 180 },
  { "shift": "NEET 2024", "occurrences": 180 }
];

const PAGE_SIZE = 10;

const getDifficultyFromLevel = (level) => {
    switch (level) {
        case 1: return 'Easy';
        case 2: return 'Medium';
        case 3: return 'Hard';
        default: return 'Medium';
    }
};

const PaperCard: React.FC<{ paper: { shift: string; occurrences: number; }, onClick: () => void, onAttempt: () => void }> = ({ paper, onClick, onAttempt }) => (
    <div className="bg-brand-surface border border-brand-border rounded-lg p-4 hover:bg-brand-primary/5 hover:border-brand-primary transition-all duration-200 group">
        <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-4 cursor-pointer" onClick={onClick}>
                <BookOpenIcon className="w-5 h-5 text-brand-text-secondary group-hover:text-brand-primary transition-colors" />
                <div>
                    <h3 className="font-semibold text-brand-text-primary">{paper.shift}</h3>
                    <p className="text-sm text-brand-text-secondary">{paper.occurrences} Questions</p>
                </div>
            </div>
            <ChevronRightIcon className="w-5 h-5 text-brand-text-secondary group-hover:text-brand-primary transition-colors cursor-pointer" onClick={onClick} />
        </div>
        <button
            onClick={onAttempt}
            className="w-full px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover transition-colors font-medium"
        >
            Attempt
        </button>
    </div>
);

export const NEETPaperWiseView = ({ user, onToggleBookmark, onNavigateToSignIn }) => {
    const navigate = useNavigate();
    const { setShowFilter, setFilterData } = useFilter();
    const [selectedPaper, setSelectedPaper] = useState<string | null>(null);
    const [questions, setQuestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activeDifficulty, setActiveDifficulty] = useState('All');
    const [activeSubject, setActiveSubject] = useState('All');
    const [activeChapter, setActiveChapter] = useState('All');
    const [currentPage, setCurrentPage] = useState(1);
    const [showTestConfig, setShowTestConfig] = useState(false);
    const [selectedPaperForTest, setSelectedPaperForTest] = useState(null);


    useEffect(() => {
        if (!selectedPaper) {
            setQuestions([]);
            return;
        }

        const fetchQuestions = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const { data, error: dbError } = await supabase
                    .from('neet')
                    .select('*')
                    .eq('shift', selectedPaper);

                if (dbError) throw dbError;

                if (data) {
                    const formattedQuestions = data.map((q) => ({
                        id: q.id.toString(),
                        text: q.question,
                        options: [q.option1, q.option2, q.option3, q.option4].filter(opt => opt),
                        answer: q[q.correct] ?? q.correct,
                        solution: q.explanation,
                        subject: q.subject || 'General',
                        topic: q.tags || q.chapter || 'Unknown Topic',
                        difficulty: getDifficultyFromLevel(q.level),
                        type: q.type || 'Single Choice',
                    }));
                    setQuestions(formattedQuestions);
                }
            } catch (err) {
                console.error('Error fetching paper questions:', err);
                setError('Failed to load questions for this paper. Please try again later.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchQuestions();
    }, [selectedPaper]);
    
    const filteredQuestions = useMemo(() => {
        if (activeDifficulty === 'All' && activeSubject === 'All' && activeChapter === 'All') {
            return questions;
        }
        return questions.filter(q => 
            (activeDifficulty === 'All' || q.difficulty === activeDifficulty) &&
            (activeSubject === 'All' || q.subject === activeSubject) &&
            (activeChapter === 'All' || q.topic === activeChapter)
        );
    }, [questions, activeDifficulty, activeSubject, activeChapter]);

    const subjects = useMemo(() => ['All', ...new Set(questions.map(q => q.subject))], [questions]);
    const chapters = useMemo(() => {
        const filteredBySubject = activeSubject === 'All' 
            ? questions 
            : questions.filter(q => q.subject === activeSubject);
        return ['All', ...new Set(filteredBySubject.map(q => q.topic))];
    }, [questions, activeSubject]);

    useEffect(() => {
        setCurrentPage(1);
    }, [activeDifficulty, activeSubject, activeChapter]);

    useEffect(() => {
        if (activeSubject !== 'All') {
            setActiveChapter('All');
        }
    }, [activeSubject]);

    const totalPages = Math.ceil(filteredQuestions.length / PAGE_SIZE);
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const questionsForCurrentPage = filteredQuestions.slice(startIndex, startIndex + PAGE_SIZE);

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
            document.querySelector('main')?.scrollTo(0, 0);
        }
    };

    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
            document.querySelector('main')?.scrollTo(0, 0);
        }
    };

    const handlePaperSelect = (paperName: string) => {
        setSelectedPaper(paperName);
        setActiveDifficulty('All');
        setActiveSubject('All');
        setActiveChapter('All');
        setCurrentPage(1);
        window.scrollTo(0, 0);
    };

    const handleBackToPapers = () => {
        setSelectedPaper(null);
        setError(null);
        setQuestions([]);
        setActiveDifficulty('All');
        setActiveSubject('All');
        setActiveChapter('All');
        setCurrentPage(1);
        setShowFilter(false);
    };

    useEffect(() => {
        if (selectedPaper && questions.length > 0) {
            setFilterData({
                subjects,
                chapters,
                activeSubject,
                activeChapter,
                activeDifficulty,
                onSubjectChange: setActiveSubject,
                onChapterChange: setActiveChapter,
                onDifficultyChange: setActiveDifficulty,
                questionCount: filteredQuestions.length,
                totalQuestions: questions.length
            });
            setShowFilter(true);
        } else {
            setShowFilter(false);
        }
    }, [selectedPaper, questions, subjects, chapters, activeSubject, activeChapter, activeDifficulty, filteredQuestions.length, setFilterData, setShowFilter]);

    const handleAttemptPaper = (paper) => {
        setSelectedPaperForTest(paper);
        setShowTestConfig(true);
    };

    const handleStartTest = (test) => {
        if (!user) {
            sessionStorage.setItem('redirectPath', window.location.pathname);
            navigate('/signin');
            return;
        }
        
        navigate(`/test/${test.id}/instructions`, { 
            state: { test }
        });
    };

    if (selectedPaper) {
        return (
            <div className="animate-fade-in space-y-6">
                <header>
                    <button onClick={handleBackToPapers} className="flex items-center space-x-2 text-sm font-semibold text-brand-text-secondary hover:text-brand-primary transition-colors mb-4">
                        <ArrowLeftIcon className="w-5 h-5" />
                        <span>Back to Papers</span>
                    </button>
                    <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-brand-text-primary">{selectedPaper}</h1>
                    <p className="mt-2 text-lg text-brand-text-secondary">
                        Practice questions from this paper with advanced filtering options.
                    </p>
                </header>



                {isLoading && (
                    <div className="flex-1 flex items-center justify-center min-h-[calc(100vh-400px)]">
                        <div className="flex flex-col items-center">
                            <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
                            <p className="mt-4 text-brand-text-secondary">Fetching questions...</p>
                        </div>
                    </div>
                )}
                
                {error && (
                    <div className="text-center py-16 bg-brand-surface rounded-lg border border-brand-border border-red-200">
                        <h3 className="text-lg font-semibold text-brand-accent-red">An Error Occurred</h3>
                        <p className="text-brand-text-secondary mt-1">{error}</p>
                    </div>
                )}
                
                {!isLoading && !error && (
                    filteredQuestions.length > 0 ? (
                        <div className="space-y-6">
                            {questionsForCurrentPage.map(q => {
                                const isBookmarked = !!user?.bookmarks?.questions?.[q.id];
                                return <QuestionCard key={q.id} question={q} user={user} isBookmarked={isBookmarked} onToggleBookmark={onToggleBookmark} onNavigateToSignIn={onNavigateToSignIn} />;
                            })}

                            {totalPages > 1 && (
                                <div className="flex items-center justify-between mt-8 pt-4 border-t border-brand-border">
                                    <button
                                        onClick={handlePrevPage}
                                        disabled={currentPage === 1}
                                        className="px-4 py-2 text-sm font-semibold rounded-lg border border-brand-border bg-brand-surface hover:bg-gray-100 dark:hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Previous
                                    </button>
                                    <span className="text-sm font-semibold text-brand-text-secondary">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <button
                                        onClick={handleNextPage}
                                        disabled={currentPage === totalPages}
                                        className="px-4 py-2 text-sm font-semibold rounded-lg border border-brand-border bg-brand-surface hover:bg-gray-100 dark:hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-16 bg-brand-surface rounded-lg border border-brand-border">
                            <h3 className="text-lg font-semibold text-brand-text-primary">No Questions Found</h3>
                            <p className="text-brand-text-secondary mt-1">
                                {questions.length > 0 
                                    ? `No questions match your current filters. Try adjusting your selection.`
                                    : `We couldn't find any questions for this paper yet. Please check back later.`
                                }
                            </p>
                        </div>
                    )
                )}
            </div>
        );
    }

    return (
        <div className="animate-fade-in space-y-6">
            <header>
                <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-brand-text-primary">NEET: Previous Year Papers</h1>
                <p className="mt-2 text-lg text-brand-text-secondary">
                    Select a paper to begin your practice session with advanced filtering.
                </p>
            </header>

            <div className="mt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {papersData.map(paper => (
                        <PaperCard 
                            key={paper.shift} 
                            paper={paper} 
                            onClick={() => handlePaperSelect(paper.shift)}
                            onAttempt={() => handleAttemptPaper(paper)}
                        />
                    ))}
                </div>
            </div>
            
            {showTestConfig && (
                <TestConfigModal
                    paper={selectedPaperForTest}
                    onClose={() => setShowTestConfig(false)}
                    onStartTest={handleStartTest}
                    exam="NEET"
                    user={user}
                />
            )}
        </div>
    );
};